import { useState } from 'react'
import { useWallet, useConnection } from '@solana/wallet-adapter-react'
import { useDagProgram } from './useDagProgram'
import { useAccount } from './useAccount'
import { api } from '~/services/api'
import { CreateDAGRequest, CreateDAGResponse } from '~/types/api'

interface GameCreationData {
  game: {
    name: string
    description: string
    readme: string
  }
  token: {
    icon: string
    symbol: string
    name: string
    description: string
  }
  distribution: {
    snipeAmount: number
  }
  repository: {
    name: string
    createType: 'new' | 'fork'
    sourceRepositoryUrl?: string
  }
}

interface GameCreationResult {
  projectPda: string
  tokenMint: string
  transactionSignature: string
  dag: CreateDAGResponse['dag']
}

interface TransactionResult {
  projectPda: string
  tokenMint: string
  transactionSignature: string
}

export const useGameCreation = () => {
  const { publicKey } = useWallet()
  const { connection } = useConnection()
  const { initializeProject } = useDagProgram()
  const { isAuthenticated, checkGitHubConnection } = useAccount()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [transactionResult, setTransactionResult] = useState<TransactionResult | null>(null)
  const [isConfirming, setIsConfirming] = useState(false)
  const [isCreatingDAG, setIsCreatingDAG] = useState(false)

  const createGame = async (data: GameCreationData): Promise<GameCreationResult> => {
    if (!publicKey) {
      throw new Error('Wallet not connected')
    }

    if (!isAuthenticated) {
      throw new Error('Please sign in first')
    }

    setIsLoading(true)
    setError(null)
    setTransactionResult(null)

    try {
      // Check GitHub connection
      const hasGitHub = await checkGitHubConnection()
      if (!hasGitHub) {
        throw new Error('GitHub account must be connected to create a DAG')
      }

      // Step 1: Initialize project on-chain
      console.log('Creating project on-chain...')
      const { projectPda, tokenMint, tx } = await initializeProject({
        name: data.token.name,
        symbol: data.token.symbol,
        description: data.token.description,
        icon: data.token.icon,
      })

      console.log('Project created on-chain:', {
        projectPda: projectPda.toBase58(),
        tokenMint: tokenMint.publicKey.toBase58(),
        tx
      })

      const txResult: TransactionResult = {
        projectPda: projectPda.toBase58(),
        tokenMint: tokenMint.publicKey.toBase58(),
        transactionSignature: tx,
      }
      setTransactionResult(txResult)

      // Step 2: Wait for transaction confirmation
      console.log('Waiting for transaction confirmation...')
      setIsConfirming(true)
      await connection.confirmTransaction(tx, 'finalized')
      console.log('Transaction confirmed!')
      setIsConfirming(false)

      // Add a small delay to ensure account propagation
      console.log('Waiting for account propagation...')
      await new Promise(resolve => setTimeout(resolve, 3000))

      // Step 3: Create DAG through API with retry logic
      setIsCreatingDAG(true)
      const dagResponse = await createDAGWithRetry(data.repository, txResult.projectPda, data.game.readme)
      setIsCreatingDAG(false)

      console.log('DAG created through API:', dagResponse)

      return {
        ...txResult,
        dag: dagResponse.dag,
      }
    } catch (err) {
      console.error('Game creation failed:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to create game'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setIsLoading(false)
      setIsConfirming(false)
      setIsCreatingDAG(false)
    }
  }

  const createDAGWithRetry = async (
    repository: GameCreationData['repository'],
    projectPda: string,
    readme: string
  ): Promise<CreateDAGResponse> => {
    const dagRequest: CreateDAGRequest = {
      projectPda,
      createType: repository.createType,
      repositoryName: repository.name,
      sourceRepositoryUrl: repository.sourceRepositoryUrl || undefined,
      readme: readme || undefined,
    }

    console.log('Creating DAG through API...')
    
    // Retry logic with exponential backoff
    const maxRetries = 5;
    const baseDelay = 2000; // 2 seconds
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`API attempt ${attempt}/${maxRetries}...`);
        return await api.dags.create(dagRequest);
      } catch (error) {
        console.log(`API attempt ${attempt} failed:`, error);
        
        if (attempt === maxRetries) {
          throw error;
        }
        
        // Wait with exponential backoff
        const delay = baseDelay * Math.pow(2, attempt - 1);
        console.log(`Waiting ${delay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw new Error('Failed to create DAG after all retries');
  }

  const retryDAGCreation = async (data: GameCreationData): Promise<CreateDAGResponse> => {
    if (!transactionResult) {
      throw new Error('No transaction result available for retry')
    }

    setIsCreatingDAG(true)
    setError(null)

    try {
      const dagResponse = await createDAGWithRetry(data.repository, transactionResult.projectPda, data.game.readme)
      console.log('DAG created successfully on retry:', dagResponse)
      return dagResponse
    } catch (err) {
      console.error('DAG creation retry failed:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to create DAG'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setIsCreatingDAG(false)
    }
  }

  return {
    createGame,
    retryDAGCreation,
    isLoading,
    isConfirming,
    isCreatingDAG,
    error,
    transactionResult,
    clearError: () => setError(null),
  }
} 